import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { isEmpty } from 'lodash';

import { ProfileContext } from '../contexts/ProfileContext';
import {
  getStrengths,
  postStrengths,
  storeStats,
  getVIPStrengths,
  fetchProjectInfo
} from '../service';
import { getSortedList, getTranslation } from '../utils';
import SelectionCard from '../components/ui/selectionCard';
import Loader from '../components/common/Loader';
import { CaretLeftIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import Button from '../components/ui/button';
import { Spinner } from '../components/common';
import { removeDuplicatesByKey } from '../utils';

export default function PgStrengths() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [profile, setProfileData] = useContext(ProfileContext);
  const { masterData: { translationPrefix, customConfigurations: { colors } = {} } = {} } = profile;

  const [strengths, setStrengths] = useState([]);
  const [selectedStrengths, setSelectedStrengths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vipStrengths, setVipStrengths] = useState([]);
  const [strengthsFiltered, setStrengthsFiltered] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    getPageData();
  }, []);

  useEffect(() => {
    // Pre-select existing strengths from profile
    if (profile?.strengths && strengths.length > 0) {
      const preSelected = strengths.filter(({ _id }) => profile.strengths.includes(_id));
      setSelectedStrengths(preSelected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.strengths, strengths]);

  const getPageData = async () => {
    try {
      if (!profile?.loaded) return;
      const {
        data: { data: strengthData }
      } = await getStrengths(profile.masterOrganization);

      const sortedStrengths = getSortedList(strengthData, i18n.language);
      setStrengths(sortedStrengths);
    } catch (e) {
      console.log('ERROR', e);
    } finally {
      setLoading(false);
    }
  };

  function getChampionProject(response, projectId) {
    const types =
      response?.portalDetails?.externalProjectTypes ||
      response?.externalProjectTypes || // fallback if file shape differs
      {};

    for (const [key, cfg] of Object.entries(types)) {
      if (cfg && cfg.projectId === projectId) {
        return {
          externalProjectTypeKey: key,
          project: cfg
        };
      }
    }
    return null;
  }

  useEffect(() => {
    const { e, n, f, t } = profile.onboardingData?.vipConfig || {};
    if (!profile?.loaded || !e || !n || !f || !t) return;
    fetchVipStrengths();
  }, []);

  const fetchVipStrengths = async () => {
    try {
      const { data: response } = await fetchProjectInfo(
        profile.onboardingData?.vipConfig?.org,
        profile.onboardingData?.vipConfig?.team
      );

      const {
        project: {
          vipConfig: { serverUrl }
        }
      } = getChampionProject(response, profile.onboardingData?.vipConfig.orgPrefix);
      const { s, n, f, t } = profile.onboardingData?.vipConfig || {};
      const {
        data: { data = [] }
      } = await getVIPStrengths(serverUrl, {
        s,
        n,
        f,
        t,
        lang: i18n.language
      });

      setVipStrengths(data.sort((a, b) => b.relevance - a.relevance));
    } catch (e) {
      console.log('ERROR', e);
    }
  };

  useEffect(() => {
    let resultStrengths = [];

    if (strengthsFiltered) return;

    if (strengths.length > 0 && vipStrengths.length > 0) {
      for (let i = 0; resultStrengths.length < 12 && i < strengths.length; i++) {
        const vipStrength = vipStrengths[i];

        if (!isEmpty(vipStrength)) {
          const orgStrength = strengths.find(
            (orgStrength) =>
              orgStrength?.[`vip_id_${i18n.language}`] === vipStrength.id ||
              orgStrength?.alternate_vip_ids?.[i18n.language]?.includes(vipStrength.id)
          );
          if (orgStrength) {
            resultStrengths.push({ ...orgStrength });
          }
        }
      }

      setStrengths(resultStrengths);
      setStrengthsFiltered(true);
    }
  }, [strengths, vipStrengths]);

  const toggleSelection = (item) => {
    const isSelected = selectedStrengths.find((s) => s._id === item._id);

    if (isSelected) {
      // Remove from selection
      setSelectedStrengths(selectedStrengths.filter((s) => s._id !== item._id));
    } else {
      // Add to selection (if not at max)
      if (selectedStrengths.length < 3) {
        setSelectedStrengths([...selectedStrengths, item]);
      }
    }
  };

  const handleNext = async () => {
    if (selectedStrengths.length !== 3) {
      return;
    }

    setSaving(true);

    try {
      const filteredStrengths = selectedStrengths.map(({ _id }) => _id);
      const body = { strengths: filteredStrengths };

      await postStrengths(body);

      // Store stats for strengths
      try {
        for (let selectedStrength of selectedStrengths) {
          await storeStats({
            application: 'champion',
            event: 'champion_chosen_strength',
            orgPrefix: translationPrefix,
            pageURL: window?.location?.pathname,
            pageTitle: document?.title,
            championID: profile?._id,
            championName: `${profile?.firstName} ${profile?.familyName}`,
            strengthName: selectedStrength[`name_${i18n.language}`]
          });
        }
      } catch (error) {
        console.error('Error in storing stats for selected strengths');
      }

      let updatedProfile = {
        ...profile,
        strengths: filteredStrengths,
        isUpdatedForPDF: true,
        validationReduested: false
      };

      // Update videos for strength if it exists
      if (profile.videos?.videos?.length) {
        const updatedVideos = [...profile.videos.videos];
        // Check first video for strength
        if (
          updatedVideos[0]?.strengthId?._id &&
          !filteredStrengths.includes(updatedVideos[0].strengthId._id)
        ) {
          updatedVideos[0] = { ...updatedVideos[0] };
          delete updatedVideos[0].strengthId;
        }

        updatedProfile.videos = {
          ...profile.videos,
          videos: updatedVideos
        };
      }

      setProfileData(updatedProfile);

      // Navigate to next incomplete step or back to profile
      if (!profile.roles || profile.roles.length === 0) {
        navigate('/roles');
      } else {
        navigate('/my-profile');
      }
    } catch (error) {
      toast.error('Some error occurred with updating the strengths!');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/my-profile');
  };

  const displayedStrengths =
    !strengthsFiltered && searchQuery
      ? strengths.filter((item) =>
          item[`name_${i18n.language}`]?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : strengths;

  if (loading) return <Loader />;

  return (
    <>
      {/* Scrollable Body */}
      <div className="fixed top-24 bottom-20 left-0 right-0 overflow-y-auto">
        <div className="px-6 py-8">
          <div className="max-w-2xl mx-auto">
            {/* Back Button and Heading */}
            <div className="relative mb-2">
              {/* Back Button - Left Aligned */}
              <button
                onClick={handleBack}
                style={{ backgroundColor: colors?.primary || '#2B4B8C' }}
                className="absolute left-0 top-1/4 -translate-y-1/2 text-white p-1.5 rounded-lg hover:opacity-90 transition-opacity">
                <CaretLeftIcon size={16} />
              </button>

              {/* Heading - Centered */}
              <h1
                style={{ color: colors?.primary || '#2B4B8C' }}
                className="text-3xl font-bold text-center"
                dangerouslySetInnerHTML={{
                  __html: getTranslation(t, 'strengthsPage.title', translationPrefix)
                }}
              />
            </div>

            {/* Description */}
            <p className="text-gray-600 my-8 text-center text-lg max-w-md mx-auto">
              <span
                dangerouslySetInnerHTML={{
                  __html: getTranslation(t, 'strengthsPage.description', translationPrefix)
                }}
              />
            </p>

            {!strengthsFiltered && (
              <div className="flex gap-3 w-full items-center mb-6">
                <MagnifyingGlassIcon size={20} className="text-gray-500 shrink-0" />
                <input
                  className="bg-gray-200 text-black rounded-full px-4 py-3 w-full"
                  placeholder={getTranslation(t, 'searchListPlaceholder', translationPrefix)}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            )}

            {/* Selection Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {displayedStrengths.map((item) => (
                <SelectionCard
                  key={item._id}
                  item={item}
                  isSelected={!!selectedStrengths.find((s) => s._id === item._id)}
                  onToggle={() => toggleSelection(item)}
                  language={i18n.language}
                  primaryColor={colors?.primary}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-300 px-6 py-6 z-40">
        <div className="max-w-2xl mx-auto flex justify-center">
          <Button.ButtonRounded
            onClick={handleNext}
            disabled={selectedStrengths.length !== 3 || saving}
            bgColor="secondary"
            className="w-full max-w-xs py-3">
            <Spinner isSubmitting={saving} />
            {getTranslation(t, 'strengthsPage.confirmButton', translationPrefix)}
          </Button.ButtonRounded>
        </div>
      </div>

      {saving && <Loader transparent={true} />}
    </>
  );
}
